/**
 * ===============================================================================
 * CONFIGURACIÓN DE TAMAÑO Y POSICIONAMIENTO DEL WIDGET DE CHAT (Clínica Milos)
 * ===============================================================================
 *
 * En este archivo puedes ajustar MANUALMENTE el tamaño (ancho y alto) y la
 * posición (izq/der/arriba/abajo) del widget de chat para cada página y resolución.
 *
 * PROPIEDADES DISPONIBLES EN CADA PERFIL:
 * -------------------------------------------------------------------------------
 * - width:       Ancho del widget en píxeles (ej: 420) o string (ej: "40%")
 * - height:      Alto del widget en píxeles (ej: 500) o string (ej: "60%")
 * - right:       Distancia desde el borde derecho (ej: 150, "20%", Math.round(w * 0.20))
 * - left:        Distancia desde el borde izquierdo (ej: 50, "15%") o 'center' para centrar
 * - top:         Distancia desde el borde superior (ej: 80, "10%") o 'center' para centrar
 * - bottom:      Distancia desde el borde inferior (ej: 25, "5%")
 *
 * PERFILES DE DISPOSITIVO:
 * -------------------------------------------------------------------------------
 * - default:     Ajuste base por defecto (fallback)
 * - fhd:         Monitores grandes Full HD / 2K / 4K (ancho >= 1800px)
 * - desktop:     Monitores de escritorio estándar (ancho 1440px - 1799px)
 * - mac:         Portátiles / MacBook / Ventana con F12 abierta (1025px - 1439px)
 * - ipad:        Tablets estándar (iPad, Galaxy Tab 768px - 1024px)
 * - ipadPro:     Tablets grandes (iPad Pro 11"/12.9", Surface Pro 1025px+)
 * - surfacePro7: Microsoft Surface Pro
 * - mobile:      Móviles en vertical (< 768px)
 * - mobileLandscape: Móviles en orientación horizontal
 * - se, i12, xr, pixel7, s8, zFold, zenbookFold: Perfiles móviles específicos
 * ===============================================================================
 */

// ===============================================================================
// 1. RECEPCIÓN (PÁGINA 1)
// ===============================================================================
export const RECEPTION_WIDGET_CONFIG = (resolution, isLandscape) => {
  const w = resolution.width;
  const h = resolution.height;

  return {
    default: { width: 420, height: 500, left: 'center', bottom: 25 },
    seLand: { width: 420, height: 140, left: 'center', bottom: 5 },
    fhd: { width: 450, height: 530, right: Math.round(w * 0.21), bottom: 25 },
    desktop: { width: 420, height: 500, right: Math.round(w * 0.20), bottom: 25 },
    mac: isLandscape ? { width: 620, height: 260, left: 'center', bottom: 20 } : { width: 1300, height: 460, left: 'center', bottom: 20 },
    ipad: isLandscape
      ? { width: 400, height: 160, left: 'center', bottom: 20 }
      : { width: Math.min(600, Math.round(w * 0.9)), height: Math.round(h * 0.38), left: 'center', bottom: 15 },
    ipadPro: isLandscape
      ? { width: 420, height: 320, left: 'center', bottom: 25 }
      : { width: Math.min(700, Math.round(w * 0.9)), height: Math.round(h * 0.38), left: 'center', bottom: 20 },
    surfacePro7: isLandscape
      ? { width: 400, height: 450, left: 'center', bottom: 20 }
      : { width: Math.min(600, Math.round(w * 0.9)), height: Math.round(h * 0.38), left: 'center', bottom: 15 },
    surfacePro7Land: { width: 400, height: 200, right: 20, bottom: 5 },
    mobileLandscape: {
      width: Math.min(430, Math.round(w * 0.50)),
      height: Math.round(h * 0.65),
      left: 'center',
      bottom: 10
    },
    mobile: {
      width: Math.round(w * 0.92),
      height: isLandscape ? Math.round(h * 0.48) : Math.round(h * 0.38),
      left: 'center',
      bottom: 10
    },
    // Ajustes finos para modelos de móvil específicos
    se: { width: Math.round(w * 0.94), height: Math.round(h * 0.38), left: 'center', bottom: 8 },
    i12: { width: Math.round(w * 0.92), height: Math.round(h * 0.38), left: 'center', bottom: 10 },
    i14ProMax: { width: Math.round(w * 0.92), height: Math.round(h * 0.38), left: 'center', bottom: 10 },
    xr: { width: Math.round(w * 0.92), height: Math.round(h * 0.35), left: 'center', bottom: 10 },
    pixel7: { width: Math.round(w * 0.92), height: Math.round(h * 0.35), left: 'center', bottom: 10 },
    s8: { width: Math.round(w * 0.94), height: Math.round(h * 0.38), left: 'center', bottom: 8 },
    s8Land: { width: Math.round(w * 0.52), height: Math.round(h * 0.38), left: 'center', bottom: 8 },
    zFold: { width: Math.round(w * 0.92), height: Math.round(h * 0.38), left: 'center', bottom: 10 },
    zFoldLand: { width: Math.round(w * 0.45), height: Math.round(h * 0.38), left: 'center', bottom: 10 },
    zenbookFold: { width: Math.round(w * 0.85), height: Math.round(h * 0.45), left: 'center', bottom: 20 },
    zenbookFoldLand: { width: Math.round(w * 0.52), height: Math.round(h * 0.38), left: 'center', bottom: 8 },
  };
};

// ===============================================================================
// 2. SALAS DE CONSULTA GENERALES (Páginas 22, 24, 26, 32, 34, 36)
// ===============================================================================
export const ROOMS_WIDGET_CONFIG = (resolution, isLandscape) => {
  const w = resolution.width;
  const h = resolution.height;

  return {
    default: { width: 400, height: 490, right: Math.max(120, Math.round(w * 0.10)), bottom: 25 },
    fhd: { width: 440, height: 530, right: Math.max(180, Math.round(w * 0.15)), bottom: 30 },
    desktop: { width: 410, height: 490, right: Math.max(140, Math.round(w * 0.12)), bottom: 25 },
    mac: { width: 380, height: 460, right: 40, bottom: 20 },
    ipad: isLandscape
      ? { width: 370, height: 220, right: 10, bottom: 10 }
      : { width: Math.min(580, Math.round(w * 0.9)), height: Math.round(h * 0.40), left: 'center', bottom: 15 },
    ipadPro: isLandscape
      ? { width: 410, height: 480, right: 10, bottom: 10 }
      : { width: Math.min(680, Math.round(w * 0.88)), height: Math.round(h * 0.40), left: 'center', bottom: 15 },
    surfacePro7: isLandscape
      ? { width: 370, height: 430, right: Math.max(60, Math.round(w * 0.08)), bottom: 15 }
      : { width: Math.min(580, Math.round(w * 0.9)), height: Math.round(h * 0.40), left: 'center', bottom: 15 },
    mobileLandscape: {
      width: Math.min(410, Math.round(w * 0.48)),
      height: Math.round(h * 0.65),
      right: 35,
      bottom: 10
    },
    // Ajustes finos para modelos de móvil específicos
    mobile: { width: Math.round(w * 0.8), height: Math.round(h * 0.4), left: 'center', bottom: 10 },
    se: { width: Math.round(w * 0.94), height: Math.round(h * 0.4), left: 'center', bottom: 8 },
    seLand: { width: Math.round(w * 0.4), height: Math.round(h * 0.55), right: 10, bottom: 10 },
    i12: { width: Math.round(w * 0.92), height: Math.round(h * 0.4), left: 'center', bottom: 10 },
    i14ProMax: { width: Math.round(w * 0.92), height: Math.round(h * 0.4), left: 'center', bottom: 10 },
    s8Land: { width: Math.round(w * 0.4), height: Math.round(h * 0.55), right: 10, bottom: 10 },
    xr: { width: Math.round(w * 0.92), height: Math.round(h * 0.4), left: 'center', bottom: 10 },
    pixel7: { width: Math.round(w * 0.92), height: Math.round(h * 0.4), left: 'center', bottom: 10 },
    s8: { width: Math.round(w * 0.94), height: Math.round(h * 0.4), left: 'center', bottom: 8 },
    zFold: { width: Math.round(w * 0.92), height: Math.round(h * 0.4), left: 'center', bottom: 10 },
    zFoldLand: { width: Math.round(w * 0.40), height: Math.round(h * 0.5), right: 10, bottom: 10 },
    zenbookFold: { width: Math.round(w * 0.85), height: Math.round(h * 0.4), right: Math.round(w * 0.10), bottom: 20 },
    zenbookFoldLand: { width: Math.round(w * 0.38), height: Math.round(h * 0.6), right: 10, bottom: 10 },
  };
};

// ===============================================================================
// 3. CONFIGURACIONES ESPECÍFICAS POR SALA (Opcional para personalización única)
// Si una sala no se define aquí, utiliza automáticamente ROOMS_WIDGET_CONFIG
// ===============================================================================
export const ROOM_CUSTOM_CONFIGS = {
  // 22: Sala Terapia de Ansiedad y Depresión
  // 24: Sala Terapia de Parejas
  // 26: Sala Crecimiento Personal
  // 32: Sala Test/Evaluación TDAH
  // 34: Sala TDAH Adultos
  // 36: Sala Asesoramiento Familias
};

// ===============================================================================
// 4. TÍTULOS DINÁMICOS DEL WIDGET SEGÚN LA SALA / PÁGINA
// ===============================================================================
export const WIDGET_TITLES = {
  1: "Chat Online - Recepción",
  21: "Chat Online - Ansiedad y Depresión",
  22: "Chat Online - Ansiedad y Depresión",
  23: "Chat Online - Terapia de Parejas",
  24: "Chat Online - Terapia de Parejas",
  25: "Chat Online - Crecimiento Personal",
  26: "Chat Online - Crecimiento Personal",
  31: "Chat Online - Test y Evaluación TDAH",
  32: "Chat Online - Test y Evaluación TDAH",
  33: "Chat Online - TDAH Adultos",
  34: "Chat Online - TDAH Adultos",
  35: "Chat Online - Asesoramiento Familias",
  36: "Chat Online - Asesoramiento Familias",
};

export const getWidgetTitle = (pageId) => {
  return WIDGET_TITLES[pageId] || "Chat Online - Clínica Milos";
};

// ===============================================================================
// 5. FUNCIÓN RESOLVEDORA PRINCIPAL POR PÁGINA
// ===============================================================================
export const getWidgetConfig = (pageId, resolution, isLandscape) => {
  // Si la sala tiene una configuración personalizada exclusiva:
  if (ROOM_CUSTOM_CONFIGS[pageId]) {
    return ROOM_CUSTOM_CONFIGS[pageId](resolution, isLandscape);
  }

  // Recepción (Página 1)
  if (pageId === 1) {
    return RECEPTION_WIDGET_CONFIG(resolution, isLandscape);
  }

  // Salas de consulta (22, 24, 26, 32, 34, 36)
  if ([22, 24, 26, 32, 34, 36].includes(pageId)) {
    return ROOMS_WIDGET_CONFIG(resolution, isLandscape);
  }

  // Fallback por defecto para cualquier otra página
  return ROOMS_WIDGET_CONFIG(resolution, isLandscape);
};

// Alias de retrocompatibilidad
export const RECEPTION_CHAT_CONFIG = RECEPTION_WIDGET_CONFIG;
export const SERVICE_PAGES_CHAT_CONFIG = (isLandscape, resolution) => ROOMS_WIDGET_CONFIG(resolution, isLandscape);

